/* eslint-disable no-restricted-syntax */

type ErrorCodes = 401 | 500 | 4000001 | 4000005 | 4000006 | 4000007 | 4000010;
interface IrrelevantResponseProperties {
  id: string;
  expires_at: Date;
  issued_at: Date;
  request_url: string;
  type: string;
}

interface IrrelevantFieldValidationProperties {
  group: string;
  meta: unknown;
  type: 'input';
  attributes: {
    name: string;
    type: 'checkbox' | 'email' | 'text' | 'number' | 'hidden';
    node_type: 'input';
    required?: boolean;
    disabled?: boolean;
  };
}

interface ValidationContext {
  reason: string;
}

interface UIValidation {
  context?: ValidationContext;
  id: number;
  text: string;
  type: 'error';
}

interface FieldValidation extends IrrelevantFieldValidationProperties {
  messages?: UIValidation[]; // ?
}

interface KratosUI {
  action: string;
  messages?: UIValidation[];
  nodes?: FieldValidation[];
}

interface KratosError {
  id: string;
  code: number;
  status: string;
}

interface KratosResponseData extends IrrelevantResponseProperties {
  ui?: KratosUI;
  error?: KratosError;
}

interface ActualError {
  response: {
    data: KratosResponseData;
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isKratosError(object: any): object is ActualError {
  return 'ui' in object.response?.data || 'error' in object.response?.data;
}

export const retrieveKratosErrors = (err: unknown) => {
  const errorsArray: ErrorCodes[] = [];

  if (isKratosError(err)) {
    const { ui, error } = err.response.data;

    if (err.response.data.id === 'session_inactive') {
      errorsArray.push(401);
      return errorsArray;
    }

    if (ui) {
      return handleUIErrors(ui);
    }

    if (error) {
      errorsArray.push(handleGlobalKratosError(error));
    }
  }

  if (!errorsArray.length) {
    errorsArray.push(500);
  }

  return errorsArray;
};

const handleUIErrors = (ui: KratosUI) => {
  const errorsFoundInUI: ErrorCodes[] = [];

  if (ui.messages?.length) {
    for (const singleError of ui.messages) {
      const errCaught = getErrorById(singleError.id);
      errorsFoundInUI.push(errCaught);
    }

    return errorsFoundInUI;
  }

  if (ui.nodes?.length) {
    ui.nodes.forEach(field => {
      if (field.messages?.length) {
        for (const singleFieldError of field.messages) {
          const errCaught = getErrorById(singleFieldError.id);
          errorsFoundInUI.push(errCaught);
        }
      }
    });
  }

  return errorsFoundInUI;
};

const handleGlobalKratosError = (err: KratosError) => {
  const error = getErrorByStatusCode(err?.code || 500);

  return error;
};

// Please visit https://www.ory.sh/docs/kratos/concepts/ui-user-interface#ui-error-codes
// to get more detailed Information
const getErrorById = (errorId: number) => {
  switch (errorId) {
    case 4000001:
    case 4000005:
    case 4000006:
    case 4000007:
    case 4000010: {
      return errorId;
    }

    default: {
      return 500;
    }
  }
};

const getErrorByStatusCode = (statusCode: number) => {
  switch (statusCode) {
    case 401: {
      return statusCode;
    }
    case 400:
    case 403:
    case 404:
    case 500:
    default: {
      return 500;
    }
  }
};
